export class AudioRecorder {
    constructor(audioEngine) {
        this.engine = audioEngine;
        this.mediaRecorder = null;
        this.chunks = [];
        this.isRecording = false;
        this.streamDestination = null;
    }

    getSupportedAudioMimeType() {
        const isApple = /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent);
        
        let types = [];
        if (isApple) {
            // Apple prioritized
            types = [
                "audio/mp4",
                "audio/aac",
                "audio/webm;codecs=opus",
                "audio/webm"
            ];
        } else {
            // Standard Web prioritized (Opus)
            types = [
                "audio/webm;codecs=opus",
                "audio/ogg;codecs=opus",
                "audio/webm",
                "audio/mp4"
            ];
        }
        
        // Add universal fallbacks
        types.push("audio/wav");
        
        for (const type of types) {
          if (MediaRecorder.isTypeSupported(type)) {
            return type;
          }
        }
        throw new Error("No supported audio mimeType found.");
    }

    setup() {
        if (!this.engine.ctx) return false;
        
        // Invalidate if context changed (e.g. after reset)
        if (this.streamDestination && this.streamDestination.context !== this.engine.ctx) {
            this.streamDestination = null;
        }

        // Create MediaStreamDestination if not exists
        if (!this.streamDestination) {
            this.streamDestination = this.engine.ctx.createMediaStreamDestination();
            // Connect Master to it
            // We need to access masterGain. 
            // Ideally AudioEngine should expose a connect method or public node.
            // Based on AudioEngine.js, masterGain is public-ish (this.masterGain)
            if (this.engine.masterGain) {
                this.engine.masterGain.connect(this.streamDestination);
            } else {
                console.error("AudioEngine MasterGain not found.");
                return false;
            }
        }
        return true;
    }

    start() {
        if (!this.setup()) return false;
        
        this.chunks = [];
        
        try {
            this.selectedMimeType = this.getSupportedAudioMimeType();
            this.mediaRecorder = new MediaRecorder(this.streamDestination.stream, {
                mimeType: this.selectedMimeType
            });
        } catch (e) {
            console.error("MediaRecorder creation failed:", e);
            return false;
        }

        this.mediaRecorder.ondataavailable = (e) => {
            if (e.data.size > 0) {
                this.chunks.push(e.data);
            }
        };

        this.mediaRecorder.onstop = () => {
            this.download();
        };

        this.mediaRecorder.start();
        this.isRecording = true;
        return true;
    }

    stop() {
        if (this.mediaRecorder && this.isRecording) {
            this.mediaRecorder.stop();
            this.isRecording = false;
            return true;
        }
        return false;
    }

    download() {
        const mimeType = this.selectedMimeType || 'audio/webm';
        const blob = new Blob(this.chunks, { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        document.body.appendChild(a);
        a.style = 'display: none';
        a.href = url;
        
        // Determine extension
        let ext = 'webm';
        if (mimeType.includes('mp4') || mimeType.includes('aac')) ext = 'mp4';
        else if (mimeType.includes('ogg')) ext = 'ogg';
        else if (mimeType.includes('wav')) ext = 'wav';

        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        a.download = `sonic-envelope-rec-${timestamp}.${ext}`;
        
        a.click();
        
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    }
}
